<template>
  <div class="practice-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">TEST SET-UP</h1>
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="isAssignment"
        :disable-navigation="questionsStarted"
      />
    </div>

    <!-- Main Content Layout -->
    <div :class="currentPhase === 'practice' ? 'recording-interface' : 'practice-interface'">
      <div :class="currentPhase === 'practice' ? 'recording-layout' : 'practice-layout'">
        <!-- Left Panel: Equipment Testing or Practice Controls -->
        <div class="left-panel">
          
          <!-- Equipment Testing Phase -->
          <div v-if="currentPhase === 'equipment'" class="equipment-section">
            <h3 class="section-title">Equipment Check</h3>
            
            <div class="equipment-tests">
              <!-- Camera Test -->
              <div class="test-item" v-if="!cameraTest.testing">
                <button
                  data-cy="practice-testCamera"
                  @click="testCamera" 
                  :class="['viva-btn', cameraTest.passed ? 'viva-btn-green' : 'viva-btn-orange']"
                  class="test-button"
                >
                  <i class="fa fa-video-camera"></i>
                  {{ cameraTest.passed ? '✓ Camera Working' : 'Test Camera' }}
                </button>
              </div>
              <div class="test-item camera-test-item" v-else>
                <div>
                  <p class="test-question">Can you see yourself in the panel to the right?</p>
                </div>
                <div class="test-buttons-row">
                  <button
                    data-cy="practice-testCamera-reset"
                    @click="cameraTest.passed = false; setupCamera()"
                    class="viva-btn viva-btn-gray test-button"
                  >Try Again</button>
                  <button
                    data-cy="practice-testCamera-confirm"
                    @click="cameraTest.passed = true; cameraTest.testing = false"
                    class="test-button viva-btn viva-btn-green"
                  >I see myself</button>
                </div>
              </div>

              <!-- Microphone Test -->
              <div class="test-item">
                <button
                  data-cy="practice-testMicrophone"
                  @click="testMicrophone" 
                  :class="['viva-btn', microphoneTest.passed ? 'viva-btn-green' : 'viva-btn-orange']"
                  class="test-button"
                >
                  <i class="fa fa-microphone"></i>
                  {{ microphoneTest.passed ? '✓ Microphone Working' : 'Test Microphone' }}
                </button>
              </div>

              <!-- Speaker Test -->
              <div class="test-item">
                <button
                  data-cy="practice-testSpeaker"
                  @click="testSpeaker" 
                  :class="['viva-btn', speakerTest.passed ? 'viva-btn-green' : 'viva-btn-orange']"
                  class="test-button"
                >
                  <i class="fa fa-volume-up"></i>
                  {{ speakerTest.passed ? '✓ Speaker Working' : 'Test Speaker' }}
                </button>
              </div>
            </div>

            <!-- Support Contact -->
            <div class="support-section">
              <p class="support-text">
                Having issues? Email us at 
                <a href="mailto:viva@initialview.com">viva@initialview.com</a>
              </p>
            </div>
          </div>

          <!-- Practice Question Phase - Using Record Page Layout -->
          <div v-if="currentPhase === 'practice'" class="controls-panel">
            <!-- Question Header -->
            <div v-if="questionsStarted" class="question-header">
              <h3 class="question-title">Practice Question</h3>
            </div>

            <!-- Timer -->
            <div v-if="questionsStarted" class="timer-section">
              <div class="timer-box">
                <div class="timer-value">{{ timeLeft }}</div>
              </div>
              <div class="timer-label">Seconds</div>
            </div>

            <!-- Question Text (for deaf/hard-of-hearing accommodation) -->
            <div v-if="questionsStarted && textQuestionsEnabled" class="question-text-section">
              <div class="question-text-box">
                <p class="question-text-content">{{ practiceQuestion }}</p>
                <p v-if="isSpeaking" class="question-text-note">
                  <i class="fa fa-volume-up"></i>
                  Interviewer is speaking...
                </p>
                <p v-else class="question-text-note question-text-ready">
                  <i class="fa fa-microphone"></i>
                  Please respond now
                </p>
              </div>
            </div>

            <!-- Waveform (shows during recording) -->
            <div v-if="questionsStarted" class="waveform-section">
              <div class="waveform-container">
                <div class="waveform-bars">
                  <div class="bar" v-for="n in 45" :key="n" :style="{ height: getBarHeight(n) }"></div>
                </div>
              </div>
            </div>


            <!-- Control Buttons -->
            <div v-if="questionsStarted" class="control-buttons">
              <button
                data-cy="practice-question-repeat"
                class="viva-btn viva-btn-orange control-btn"
                @click="repeatPracticeQuestion"
                :disabled="isSpeaking"
              >
                <i class="fa fa-volume-up"></i>
                Repeat question
              </button>
              
              <button
                data-cy="practice-question-submit"
                class="viva-btn viva-btn-green control-btn"
                @click="endPracticeRecording"
                :disabled="!isRecording || isSpeaking"
              >
                End recording
              </button>
            </div>

            <!-- Back to Equipment -->
            <div class="back-section">
              <p 
                class="back-link" 
                @click="backToEquipment"
              >
                <i class="fa fa-arrow-left"></i> Back
            </p>
            </div>
          </div>
          
          <!-- Playback Phase -->
          <div v-if="currentPhase === 'playback'" class="controls-panel">
            <div class="playback-section">
              <h3 class="section-title">Practice Recording Complete</h3>
              <p class="playback-instruction">Review your practice response in the video player to the right.</p>
              
              <button
                data-cy="practice-question-retry"
                class="viva-btn viva-btn-orange" 
                @click="retryPractice"
              >
                <i class="fa fa-refresh"></i>
                Retry practice question
              </button>
            </div>

            <!-- Back to Equipment -->
            <div class="back-section">
              <p 
                class="back-link" 
                @click="backToEquipment"
              >
                <i class="fa fa-arrow-left"></i> Back
            </p>
            </div>
          </div>
        </div>

        <!-- Right Panel: Video -->
        <div class="video-panel">
          <div v-if="currentPhase === 'equipment' || currentPhase === 'practice'" class="video-container">
            <video 
              id="practiceViewfinder" 
              autoplay 
              muted 
              playsinline
              class="recording-video"
            ></video>
            <div v-if="!cameraReady" class="camera-placeholder">
              <i class="fa fa-video-camera"></i>
              <p>Setting up camera...</p>
            </div>
            <!-- Recording Indicator -->
            <div v-if="isRecording" class="recording-indicator">
              <div class="recording-dot"></div>
              <span class="recording-text">REC</span>
            </div>
          </div>

          <div v-if="currentPhase === 'playback'" class="video-container">
            <video 
              id="reviewPlayback" 
              controls
              playsinline
            ></video>
          </div>

          <!-- Equipment Phase Button -->
          <div v-if="currentPhase === 'equipment'">
            <button
              data-cy="practice-proceedToPractice"
              @click="proceedToPractice"
              :disabled="!allEquipmentReady"
              :class="['viva-btn', 'viva-btn-orange', 'proceed-button']"
            >
              {{ allEquipmentReady ? "Equipment ready - proceed to Practice Question" : "Complete all equipment tests first" }}
            </button>
          </div>

          <!-- Practice Phase Button / Question Generation Status -->
          <div v-if="currentPhase != 'equipment'" class="question-status-wrapper">
            <!-- Questions Ready -->
            <button
              v-if="questionStatus === 'ready'"
              data-cy="practice-startExam"
              @click="startRealExam"
              class="viva-btn viva-btn-orange proceed-button"
            >
              I'm Good and Let's Start
            </button>

            <!-- Questions Generating (spinner with rotating messages) -->
            <div v-else-if="questionStatus === 'generating' || questionStatus === 'checking'" class="generation-status-section">
              <div class="generation-spinner">
                <i class="fa fa-spinner fa-spin generation-spinner-icon"></i>
                <p class="spinner-message">{{ spinnerMessages[spinnerMessageIndex] }}</p>
              </div>
            </div>

            <!-- Questions Failed / Timed Out -->
            <div v-else-if="questionStatus === 'failed'" class="generation-failed-section">
              <div class="generation-failed-content">
                <i class="fa fa-spinner fa-spin generation-spinner-icon"></i>
                <p class="generation-failed-text">
                  <strong>VIVA is really popular right now!</strong>
                </p>
                <p class="generation-failed-text">
                  <strong>If you would like to receive a text when your questions are ready, enter your phone number and we will notify you shortly.</strong>
                </p>
                <p class="generation-failed-text">
                  <strong>Please note that we have received your VIVA registration and, provided you have registered before any applicable deadlines, your target institution(s) will receive your VIVA in a timely fashion.</strong>
                </p>
                <div v-if="!phoneSubmitted" class="phone-input-section">
                  <input
                    type="tel"
                    v-model="phoneNumber"
                    placeholder="Your phone number"
                    class="phone-input"
                    @keyup.enter="submitPhoneNumber"
                  />
                  <button
                    @click="submitPhoneNumber"
                    :disabled="phoneSubmitting || !phoneNumber.trim()"
                    class="viva-btn viva-btn-orange phone-submit-btn"
                  >
                    {{ phoneSubmitting ? 'Submitting...' : 'Notify Me' }}
                  </button>
                </div>
                <p v-else class="phone-success-message">
                  Thanks! We'll text you when your questions are ready.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Audio Test Modal -->
    <div v-if="showAudioTestModal" class="viva-modal-backdrop" @click="closeAudioTestModal">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Speaker Test</h2>
        </div>
        <div class="viva-modal-body">
          <p  class="test-question">Click the button below to play a test sound:</p>
          <div class="audio-test-section">
            <button data-cy="practice-testSpeaker-playTest" @click="playTestAudio" class="viva-btn viva-btn-orange">
              <i class="fa fa-play"></i>
              Play Test Audio
            </button>
          </div>
          <p class="test-question">Can you hear the sound clearly?</p>
        </div>
        <div class="viva-modal-footer">
          <button data-cy="practice-testSpeaker-no" class="viva-btn viva-btn-gray" @click="speakerTestFailed">
            No
          </button>
          <button data-cy="practice-testSpeaker-yes" class="viva-btn viva-btn-green" @click="speakerTestPassed">
            Yes
          </button>
        </div>
      </div>
    </div>

    <!-- Microphone Test Modal -->
    <div v-if="showMicrophoneTestModal" class="viva-modal-backdrop" @click="closeMicrophoneTestModal">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Microphone Test</h2>
        </div>
        <div class="viva-modal-body">
          <div class="mic-test-section">
            <div class="mic-icon-section">
              <i class="fa fa-microphone microphone-icon"></i>
            </div>
            
            <div class="mic-level-container">
              <div 
                class="mic-level-bar"
                :style="{ width: micLevel + '%' }"
              ></div>
            </div>
            
            <div class="mic-instructions">
              <p class="mic-title">IS YOUR MICROPHONE WORKING?</p>
              <p class="mic-description">
                If your microphone is working, the grey bar above will fill with orange as you speak.
              </p>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button data-cy="practice-testMicrophone-no" class="viva-btn viva-btn-gray" @click="microphoneTestFailed">
            No
          </button>
          <button data-cy="practice-testMicrophone-yes" class="viva-btn viva-btn-green" @click="microphoneTestPassed">
            Yes
          </button>
        </div>
      </div>
    </div>

    <!-- Name Capture Modal (safety net for students routed past Upload.vue) -->
    <div v-if="showNameModal" class="viva-modal-backdrop">
      <div class="viva-modal-content">
        <div class="viva-modal-header">
          <h2>What's your name?</h2>
        </div>
        <div class="viva-modal-body">
          <p class="test-question">
            Before you begin, please tell us your name so your teacher knows whose viva this is.
          </p>
          <div class="name-modal-row">
            <input
              data-cy="practice-nameModal-firstName"
              type="text"
              v-model="nameModalFirstName"
              placeholder="First Name"
              class="form-control name-modal-input"
              :disabled="nameModalSubmitting"
            />
            <input
              data-cy="practice-nameModal-lastName"
              type="text"
              v-model="nameModalLastName"
              placeholder="Last Name"
              class="form-control name-modal-input"
              :disabled="nameModalSubmitting"
            />
          </div>
          <p v-if="nameModalError" class="name-modal-error">{{ nameModalError }}</p>
        </div>
        <div class="viva-modal-footer">
          <button
            data-cy="practice-nameModal-submit"
            class="viva-btn viva-btn-green"
            @click="submitStudentName"
            :disabled="!nameModalIsValid || nameModalSubmitting"
          >
            {{ nameModalSubmitting ? 'Saving...' : 'Continue' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ivRecorder, ivRecordingMixin } from "iv-record";
import { setEnv, ivChunkUploadMixin } from "iv-base-components";
import env from "../utils/env.js";
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData, setStatusData } from '../utils/statusCache.js';
import apiClient from '../utils/apiClient.js';

export default {
  mixins: [ivRecordingMixin, ivChunkUploadMixin],
  name: 'Practice',
  components: {
    ProfileDropdown
  },
  data() {
    return {
      // Phase control
      currentPhase: 'equipment', // 'equipment', 'practice', or 'playback'
      
      // Equipment tests
      cameraTest: { passed: false, testing: false },
      microphoneTest: { passed: false },
      speakerTest: { passed: false },
      
      // Camera and audio streams
      stream: null,
      cameraReady: false,

      // Audio system properties (from Record.vue)
      practiceAudioUrl: '/player/video/v1/file/1291d1ad-8855-42bc-a013-739d37c35da3/',
      audioContext: null,
      micSource: null,
      mixedOutputStream: null,

      // Audio test modal
      showAudioTestModal: false,
      
      // Microphone test modal
      showMicrophoneTestModal: false,
      micLevel: 0,
      
      // Practice recording
      questionsStarted: false,
      isRecording: false,
      isSpeaking: false,
      isPaused: false,
      timeLeft: 60,
      timerInterval: null,
      waveformInterval: null,
      
      // iv-record recording data
      videoRecorder: null,
      practiceRecordingUrl: null,
      
      // Speech synthesis
      // @TODO: remove these - we replaced synthesis with stored audio files, so they're no longer used
      synth: null,
      currentUtterance: null,
      
      // Practice question
      practiceQuestion: "What is something interesting that you've learned lately?",

      // Accommodation settings
      textQuestionsEnabled: false,
      timeMultiplier: 1.0,
      answerTime: 60,

      // Profile dropdown
      studentName: '',
      isAssignment: false,

      // Name capture modal (safety net for students routed past Upload.vue)
      showNameModal: false,
      nameModalFirstName: '',
      nameModalLastName: '',
      nameModalSubmitting: false,
      nameModalError: '',

      // Question generation status
      questionStatus: 'checking', // 'checking', 'ready', 'generating', 'failed'
      questionPollingInterval: null,
      spinnerMessageIndex: 0,
      messageRotationInterval: null,
      spinnerMessages: [
        "Analyzing your project...",
        "Reading through your work...",
        "Generating thoughtful questions...",
        "Crafting personalized prompts...",
        "Putting the finishing touches on your questions...",
        "Almost ready...",
      ],
      phoneNumber: '',
      phoneSubmitted: false,
      phoneSubmitting: false,
    }
  },

  computed: {
    allEquipmentReady() {
      return this.cameraTest.passed && this.microphoneTest.passed && this.speakerTest.passed;
    },
    nameModalIsValid() {
      return this.nameModalFirstName.trim().length > 0
          && this.nameModalLastName.trim().length > 0;
    },
  },

  mounted() {
    setEnv(env); // needed for iv-record
    this.synth = window.speechSynthesis;
    this.setupCamera();

    // Fetch accommodation settings
    this.fetchAccommodations();

    // Start polling for question readiness
    this.startQuestionPolling();
  },

  beforeDestroy() {
    this.stopQuestionPolling();
    this.cleanup();
  },

  methods: {
    fetchAccommodations() {
      const statusData = getStatusData();
      if (statusData) {
        // Load accommodation settings
        this.textQuestionsEnabled = statusData.text_questions_enabled || false;
        this.timeMultiplier = statusData.time_multiplier || 1.0;
        this.answerTime = statusData.answer_time || 60;

        // Store student info for profile dropdown
        if (statusData.student_name !== undefined) {
          this.studentName = statusData.student_name;
        }
        if (statusData.is_assignment !== undefined) {
          this.isAssignment = statusData.is_assignment;
        }

        // Safety net: coursework students who skipped Upload.vue (teacher-uploaded
        // assigned_file path) arrive here with blank first/last names. get_full_name()
        // returns "," → length 1 after .strip(). Matches Upload.vue's detection at
        // line 539.
        if (this.studentName.trim().length <= 1) {
          this.showNameModal = true;
        }
      }
    },

    async submitStudentName() {
      if (!this.nameModalIsValid || this.nameModalSubmitting) return;
      this.nameModalSubmitting = true;
      this.nameModalError = '';
      try {
        const payload = {
          first_name: this.nameModalFirstName.trim(),
          last_name: this.nameModalLastName.trim(),
        };
        const vparam = sessionStorage.getItem('vparam');
        if (vparam) payload.vparam = vparam;

        const response = await apiClient.post('/api/viva/update_student_name/', payload);
        if (response.data.status !== 200) {
          throw new Error(response.data.error || 'Failed to save name');
        }

        this.studentName = response.data.student_name;
        const cached = getStatusData();
        if (cached) {
          setStatusData({ ...cached, student_name: response.data.student_name });
        }
        this.showNameModal = false;
      } catch (err) {
        console.error('Error saving student name:', err);
        this.nameModalError = 'Unable to save your name. Please try again.';
      } finally {
        this.nameModalSubmitting = false;
      }
    },

    async setupCamera() {
      try {
        // Get camera and microphone with audio constraints to prevent feedback
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        if (document.getElementById('practiceViewfinder')) {
          document.getElementById('practiceViewfinder').srcObject = this.stream;
          this.cameraReady = true;

          // Setup audio mixing for recording integration
          this.setupAudioMixing();

        }
      } catch (error) {
        console.error('Camera setup failed:', error);
        alert('Camera access is required. Please allow camera permissions and refresh the page.');
      }
    },

    setupAudioMixing() {
      try {
        // Create Web Audio API context
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

        // Create source from microphone stream
        this.micSource = this.audioContext.createMediaStreamSource(this.stream);

        // Create destination for mixed audio
        this.mixedOutputStream = this.audioContext.createMediaStreamDestination();

        // Connect microphone to mixed output
        this.micSource.connect(this.mixedOutputStream);

      } catch (error) {
        console.error('Practice audio mixing setup failed:', error);
      }
    },

    async playPracticeAudio() {
      if (!this.audioContext) {
        console.error('Audio context not available');
        return;
      }

      try {
        this.isSpeaking = true;
        this.pauseTimer();
        this.startWaveformAnimation();

        // Fetch the practice audio file
        const response = await fetch(this.practiceAudioUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        // Decode audio data
        this.audioContext.decodeAudioData(arrayBuffer)
          .then(audioBuffer => {
            const source = this.audioContext.createBufferSource();
            source.buffer = audioBuffer;

            // Connect to mixed output (so it's recorded)
            source.connect(this.mixedOutputStream);

            // Also connect to speakers (so student can hear)
            source.connect(this.audioContext.destination);

            source.onended = () => {
              this.isSpeaking = false;
              this.stopWaveformAnimation();
              this.resumeTimer();
            };

            source.start();
          })
          .catch(error => {
            console.error('Error decoding practice audio:', error);
            this.isSpeaking = false;
            this.stopWaveformAnimation();
            this.resumeTimer();
          });

      } catch (error) {
        console.error('Error playing practice audio:', error);
        this.isSpeaking = false;
        this.stopWaveformAnimation();
        this.resumeTimer();
      }
    },

    testCamera() {
      this.cameraTest.testing = true;
    },

    testMicrophone() {
      this.showMicrophoneTestModal = true;
      this.startMicrophoneLevelMonitoring();
    },

    async startMicrophoneLevelMonitoring() {
      try {
        // Get audio stream for microphone testing with same constraints
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        
        // Create audio context and analyser
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        
        analyser.fftSize = 512;
        source.connect(analyser);
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        
        // Monitor microphone levels
        const checkMicActivity = () => {
          if (!this.showMicrophoneTestModal) {
            // Stop monitoring when modal is closed
            stream.getTracks().forEach(track => track.stop());
            return;
          }
          
          analyser.getByteFrequencyData(dataArray);
          const maxVolume = Math.max(...dataArray);
          
          // Scale the maxVolume to 0-100% for the progress bar
          this.micLevel = Math.min((maxVolume / 256) * 100, 100);
          
          requestAnimationFrame(checkMicActivity);
        };
        
        checkMicActivity();
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Cannot access microphone. Please check your microphone permissions.');
        this.showMicrophoneTestModal = false;
      }
    },

    testSpeaker() {
      this.showAudioTestModal = true;
    },

    playTestAudio() {
      // Create a simple beep sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);
    },

    speakerTestPassed() {
      this.speakerTest.passed = true;
      this.showAudioTestModal = false;
    },

    speakerTestFailed() {
      this.speakerTest.passed = false;
      this.showAudioTestModal = false;
      alert('Please check your speaker/headphone connection and try again.');
    },

    closeAudioTestModal() {
      this.showAudioTestModal = false;
    },

    microphoneTestPassed() {
      this.microphoneTest.passed = true;
      this.showMicrophoneTestModal = false;
      this.micLevel = 0;
    },

    microphoneTestFailed() {
      this.microphoneTest.passed = false;
      this.showMicrophoneTestModal = false;
      this.micLevel = 0;
      alert('Please check your microphone connection and try again.');
    },

    closeMicrophoneTestModal() {
      this.showMicrophoneTestModal = false;
      this.micLevel = 0;
    },

    proceedToPractice() {
      if (this.allEquipmentReady) {
        this.currentPhase = 'practice';
        // Auto-start practice question
        setTimeout(() => {
          this.startPracticeRecording();
        }, 500);
      }
    },

    async startPracticeRecording() {
      if (this.stream && this.mixedOutputStream) {
        // Use mixed audio stream that includes practice audio
        const mixedVideoStream = new MediaStream([
          ...this.stream.getVideoTracks(),
          ...this.mixedOutputStream.stream.getAudioTracks()
        ]);

        // Initialize iv-record with mixed stream
        this.videoRecorder = new ivRecorder("video", "VivaPractice", this);
        await this.videoRecorder.start(mixedVideoStream);
        this.isRecording = true;
        this.questionsStarted = true;

      }

      this.startTimer();

      // Delay audio playback to ensure recording is fully started
      setTimeout(() => {
        this.playPracticeAudio();
      }, 500);
    },

    repeatPracticeQuestion() {
      this.playPracticeAudio();
    },

    async endPracticeRecording() {
      if (this.videoRecorder && this.isRecording) {
        try {
          await this.videoRecorder.stop();
          this.isRecording = false;
          this.questionsStarted = false;
          clearInterval(this.timerInterval);
          this.synth?.cancel();
          this.stopWaveformAnimation();

          // Store recording for playback
          if (this.videoRecorder.blob) {
            this.practiceRecordingUrl = URL.createObjectURL(this.videoRecorder.blob);
          }
          
          // Stop camera stream to prevent it from appearing in playback
          const liveVideoElement = document.getElementById('practiceViewfinder');
          if (liveVideoElement) {
            liveVideoElement.srcObject = null;
          }
          
          // Transition to playback phase
          this.currentPhase = 'playback';
          
          // Wait for Vue to update DOM, then set video source
          this.$nextTick(() => {
            const playbackElement = document.getElementById('reviewPlayback');
            if (playbackElement && this.practiceRecordingUrl) {
              playbackElement.src = this.practiceRecordingUrl;
              playbackElement.load(); // Force reload
            } else {
              console.error('Playback video element not found or no recording URL');
            }
          });
        } catch (error) {
          console.error('Error stopping practice recording:', error);
          alert('Error saving practice recording. Please try again.');
        }
      }
    },

    startTimer() {
      // Apply time multiplier for accommodations (e.g., 1.5x = 90 seconds)
      this.timeLeft = Math.floor(this.answerTime * this.timeMultiplier);
      this.isPaused = false;
      this.timerInterval = setInterval(() => {
        if (!this.isPaused && this.timeLeft > 0) {
          this.timeLeft--;
        }
        if (this.timeLeft === 0) {
          this.timeUp();
        }
      }, 1000);
    },

    pauseTimer() {
      this.isPaused = true;
    },

    resumeTimer() {
      this.isPaused = false;
    },
    
    timeUp() {
      this.endPracticeRecording();
    },

    startWaveformAnimation() {
      this.waveformInterval = setInterval(() => {
        this.$forceUpdate();
      }, 100);
    },

    stopWaveformAnimation() {
      if (this.waveformInterval) {
        clearInterval(this.waveformInterval);
        this.waveformInterval = null;
      }
    },

    getBarHeight(index) {
      // Match Record.vue animation logic
      if (!this.isSpeaking) return '4px';
      const base = 4;
      const variation = Math.sin((Date.now() / 100) + index) * 15 + 15;
      return Math.max(base, variation) + 'px';
    },

    backToEquipment() {
      this.currentPhase = 'equipment';
      this.cleanup();
      this.setupCamera();
    },

    retryPractice() {
      // Clean up previous recording
      if (this.practiceRecordingUrl) {
        URL.revokeObjectURL(this.practiceRecordingUrl);
        this.practiceRecordingUrl = null;
      }

      // Clean up video element if it exists
      if (document.getElementById('reviewPlayback')) {
        document.getElementById('reviewPlayback').src = '';
      }
      
      // Reset to practice phase
      this.currentPhase = 'practice';
      this.timeLeft = Math.floor(this.answerTime * this.timeMultiplier);

      // Re-attach camera stream
      this.$nextTick(() => {
        this.setupCamera();
        setTimeout(() => {
          this.startPracticeRecording();
        }, 500);
      });
    },

    startRealExam() {
      // Navigate to the real exam
      this.$router.push('/record');
    },

    startQuestionPolling() {
      // Check immediately on mount
      this.checkQuestionReadiness();

      // Poll every 5 seconds
      this.questionPollingInterval = setInterval(() => {
        this.checkQuestionReadiness();
      }, 5000);

      // Advance spinner messages every 15 seconds, cycle once then stay on the last one
      this.advanceSpinnerMessage();
    },

    advanceSpinnerMessage() {
      if (this.spinnerMessageIndex < this.spinnerMessages.length - 1) {
        this.messageRotationInterval = setTimeout(() => {
          this.spinnerMessageIndex++;
          this.advanceSpinnerMessage();
        }, 15000);
      }
    },

    stopQuestionPolling() {
      if (this.questionPollingInterval) {
        clearInterval(this.questionPollingInterval);
        this.questionPollingInterval = null;
      }
      if (this.messageRotationInterval) {
        clearTimeout(this.messageRotationInterval);
        this.messageRotationInterval = null;
      }
    },

    async checkQuestionReadiness() {
      try {
        const vparam = sessionStorage.getItem('vparam');
        const params = vparam ? { vparam } : {};
        const response = await apiClient.get('/api/viva/question_readiness/', { params });
        const status = response.data.status;

        // Backend computes ready/generating/failed by checking for a current
        // VivaVideoTake with questions+audio and applying the timeout window
        // (QUESTION_GENERATION_TIMEOUT_MINUTES). Only 'ready' stops polling.
        // 'failed' keeps polling so the page can recover on its own if
        // generation finishes late - the student can still submit their phone
        // number in the meantime for a text if they'd rather not wait.
        if (status === 'ready') {
          this.questionStatus = 'ready';
          this.stopQuestionPolling();
        } else if (status === 'failed') {
          this.questionStatus = 'failed';
        } else {
          this.questionStatus = 'generating';
        }
      } catch (error) {
        console.error('Error checking question readiness:', error);
        // Don't change status on network errors - keep polling
      }
    },

    async submitPhoneNumber() {
      if (!this.phoneNumber.trim() || this.phoneSubmitting) return;

      this.phoneSubmitting = true;
      try {
        const vparam = sessionStorage.getItem('vparam');
        const payload = { phone: this.phoneNumber.trim() };
        if (vparam) payload.vparam = vparam;

        await apiClient.post('/api/viva/save_student_phone/', payload);
        this.phoneSubmitted = true;
      } catch (error) {
        console.error('Error saving phone number:', error);
        alert('Unable to save your phone number. Please try again.');
      } finally {
        this.phoneSubmitting = false;
      }
    },

    cleanup() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
      if (this.waveformInterval) {
        clearInterval(this.waveformInterval);
      }
      if (this.synth) {
        this.synth.cancel();
      }
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
      }
      
      // Clean up recording
      if (this.videoRecorder && this.isRecording) {
        try {
          this.videoRecorder.stop();
        } catch (error) {
          console.error('Error stopping recorder during cleanup:', error);
        }
      }
      
      // Clean up blob URLs
      if (this.practiceRecordingUrl) {
        URL.revokeObjectURL(this.practiceRecordingUrl);
        this.practiceRecordingUrl = null;
      }

      // Clean up Web Audio API resources
      if (this.audioContext) {
        try {
          this.audioContext.close();
          this.audioContext = null;
        } catch (error) {
          console.error('Error closing audio context:', error);
        }
      }

      this.micSource = null;
      this.mixedOutputStream = null;

      // Reset state
      this.timeLeft = Math.floor(this.answerTime * this.timeMultiplier);
      this.isRecording = false;
      this.questionsStarted = false;
      this.isSpeaking = false;
      this.cameraReady = false;
      this.cameraTest.testing = false;
      this.cameraTest.passed = false;
      this.microphoneTest.passed = false;
      this.speakerTest.passed = false;
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.practice-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
}

/* Main Layout */
.practice-interface {
  min-height: calc(100vh - 120px);
  padding: 40px 0;
}

.practice-layout {
  display: flex;
  gap: 40px;
  align-items: flex-start;
  max-width: 1400px;
  margin: 0 auto;
  justify-content: center;
  width: 100%;
  padding: 0 20px;
}

/* Left Panel */
.left-panel {
  flex: 1;
  min-width: 500px;
  max-width: 600px;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--viva-dark);
  margin-bottom: 30px;
  text-align: center;
}

/* Playback Phase Styling */
.playback-section {
  background: var(--viva-white);
  border: 2px solid var(--viva-green);
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  width: 75%;
}

.playback-instruction {
  color: var(--viva-dark);
  font-size: 18px;
  margin: 20px 0;
  line-height: 1.5;
}

/* Equipment Section */
.equipment-section {
  background: var(--viva-white);
  border: 2px solid var(--viva-green);
  border-radius: 12px;
  padding: 30px;
}

.equipment-tests {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
}

.test-item {
  display: flex;
  justify-content: center;
}

/* Camera test specific layout */
.camera-test-item {
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.test-buttons-row {
  display: flex;
  flex-direction: row;
  gap: 15px;
  justify-content: center;
}

.test-button {
  width: 100%;
  max-width: 400px;
  padding: 15px 20px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* Override for camera test buttons to be wider */
.test-buttons-row .test-button {
  max-width: 200px;
  min-width: 180px;
  flex: 1;
}

.test-button i {
  font-size: 20px;
}

.support-section {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.support-text {
  color: var(--viva-dark);
  font-size: 14px;
  margin: 0;
}

.support-text a {
  color: var(--viva-orange);
  text-decoration: none;
}

.support-text a:hover {
  text-decoration: underline;
}

.proceed-button {
  width: 100%;
  padding: 20px;
  font-size: 18px;
  font-weight: 600;
  margin-top: 20px;
}

.proceed-button:disabled {
  background-color: #ccc;
  color: #666;
  cursor: not-allowed;
  transform: none;
}

/* Question Status Wrapper - contain: inline-size prevents the generation
   sections from widening the video-panel; the video container alone
   determines the panel width, and this wrapper fills that width. */
.question-status-wrapper {
  contain: inline-size;
}

/* Question Generation Status */
.generation-status-section {
  width: 100%;
  margin-top: 20px;
  text-align: center;
}

.generation-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  border: 2px solid var(--viva-green);
}

.generation-spinner-icon {
  font-size: 32px;
  color: var(--viva-orange, #e8834a);
}

.spinner-message {
  font-size: 16px;
  color: var(--viva-dark, #333);
  font-weight: 500;
  margin: 0;
  transition: opacity 0.3s ease;
}

.generation-failed-section {
  width: 100%;
  margin-top: 20px;
}

.generation-failed-content {
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  border: 2px solid var(--viva-green);
  text-align: center;
}

.generation-failed-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--viva-dark, #333);
  margin: 0 0 8px 0;
}

.generation-failed-text {
  font-size: 15px;
  color: #555;
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.phone-input-section {
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.phone-input {
  padding: 10px 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
  width: 220px;
  outline: none;
  transition: border-color 0.2s;
}

.phone-input:focus {
  border-color: var(--viva-orange, #e8834a);
}

.phone-submit-btn {
  padding: 10px 20px;
  font-size: 16px;
}

.phone-success-message {
  font-size: 16px;
  color: #2a9d2a;
  font-weight: 500;
  margin: 0;
}

/* Audio Test Modal */
.audio-test-section {
  text-align: center;
  margin: 20px 0;
}

.test-question {
  margin-top: 20px;
  font-weight: 500;
  color: var(--viva-dark);
  font-size: 1.2em;
  text-align: center;
}

/* Microphone Test Modal */
.mic-test-section {
  text-align: center;
  padding: 20px;
}

.mic-icon-section {
  margin-bottom: 20px;
}

.microphone-icon {
  font-size: 48px;
  color: var(--viva-green);
}

.mic-level-container {
  width: 60%;
  height: 12px;
  background-color: #e0e0e0;
  margin: 20px auto;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid var(--viva-green);
}

.mic-level-bar {
  height: 100%;
  background-color: var(--viva-orange);
  border-radius: 4px;
  transition: width 0.1s ease;
}

.mic-instructions {
  margin-top: 20px;
}

.mic-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--viva-dark);
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.mic-description {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .practice-layout {
    flex-direction: column;
    gap: 30px;
  }
  
  .left-panel,
  .right-panel {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .practice-interface {
    padding: 20px 0;
  }
  
  .section-title {
    font-size: 20px;
  }
  
  .test-button {
    font-size: 16px;
  }
  
  .video-container {
    aspect-ratio: 16/9;
  }
  
  .proceed-button {
    font-size: 16px;
    padding: 15px;
  }
}

/* Question Text Section (for deaf/hard-of-hearing accommodation) */
.question-text-section {
  margin: 30px 0 20px 0;
}
.question-text-box {
  background: linear-gradient(135deg, #f5b041 0%, #f39c12 100%);
  border-radius: 12px;
  padding: 25px 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 3px solid #e67e22;
}
.question-text-content {
  font-size: 20px;
  font-weight: 600;
  color: var(--viva-white);
  margin: 0 0 15px 0;
  line-height: 1.5;
  text-align: center;
}
.question-text-note {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  text-align: center;
  font-style: italic;
}
.question-text-note i {
  margin-right: 5px;
}
.question-text-ready {
  color: #d4edda;
  font-weight: 700;
}

.name-modal-row {
  display: flex;
  gap: 15px;
  margin: 20px 0;
}
.name-modal-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
}
.name-modal-input:focus {
  border-color: var(--viva-orange, #e8834a);
  outline: none;
}
.name-modal-error {
  color: #d9534f;
  text-align: center;
  margin: 10px 0 0;
  font-size: 14px;
}
</style>